import { ViewType, ViewObject } from '../base';

import { dataService } from '../../data';
import { Extra } from '../objects/extra';
import { ExtraPeriod } from '../objects/extraperiod';
import { ExtraProduct } from '../objects/extraproduct';
import { ExtraTable } from '../objects/extratable';

import { PlaceView } from './place';
import { ProductView } from './product';
import { QrCodeView } from './qrcode';
import { TicketView } from './ticket';
import { TicketProductView } from './ticketproduct';

/****************************/
/* CLASS VIEW               */
/****************************/

export class _ExtraView extends ViewObject<Extra> {
    
    /**********************************/
    /* VIEWOBJECT IMPLEMENTATION      */
    /**********************************/

    private get extra(): Extra{
        return this.object;
    }
             
    constructor(object: Extra, data: dataService){
        super(object, data);
    }

    override OnDestroy(): void {
        super.OnDestroy();
    }

    /**********************************/
    /* VIEW MANAGED INFORMATION        */
    /**********************************/

    private _validperiods: Array <ExtraPeriod> | null = null;
    private _validextraproducts: Array <ExtraProduct> | null = null;
    private _validextratables: Array <ExtraTable> | null = null;
    private _validproducts: Array <ProductView> | null = null;
    private _validtables: Array <QrCodeView> | null = null;

    private RefreshView(targets: string[]){
        if (targets.includes('EXTRA')){
            this.DoRefreshView();
        }

        if (targets.includes('PRODUCT')){
            this._validproducts = null;
            this.DoRefreshView();
        }

        if (targets.includes('EXTRAPERIOD')){
            this._validperiods = null;
            this.DoRefreshView();
        }

        if (targets.includes('EXTRAPRODUCT')){
            this._validproducts = null;
            this._validextraproducts = null;
            this.DoRefreshView();
        }

        if (targets.includes('EXTRATABLE') || targets.includes('QRCODE')){
            this._validtables = null;
            this._validextratables = null;
            this.DoRefreshView();
        }
    }

    /**********************************/
    /* CHILDREN REGISTRATION          */
    /**********************************/

    override doRegister(): void{
        const weakref = new WeakRef(this);

        // deregister of current subscriptions
        for (const _subsription of this._subscriptions){
            _subsription.unsubscribe();
        }
        this._subscriptions = [];

         // update registrations when children change
        this._subscriptions.push(
            this.extra.OnChildChanged.subscribe(() => {
                const self = weakref.deref();
                if (self) self.doRegister();
            })
        );

        // register to updates on contained items             
        this._subscriptions.push(
            this.extra.OnRefresh.subscribe(
            (targets: string[]) => {
                const self = weakref.deref();
                if (self){  // do not keep strong references in subscription
                    self.RefreshView(targets);
                }
            })
        )

        for(const product of this.products){
            this._subscriptions.push(
                product.OnRefresh.subscribe(
                (targets: string[]) => {
                    const self = weakref.deref();
                    if (self){  // do not keep strong references in subscription
                        self.RefreshView(targets);
                    }
                })
            );
        }
    }

    /**********************************/
    /* ACTIVE CHILDREN INFORMATION    */
    /**********************************/
    
    get place(): PlaceView | null {
        return this.data.alives.get(this.extra.place) as PlaceView;
    }

    get periods(): Array <ExtraPeriod> {
        if (this._validperiods == null){
            const _periods: Array <ExtraPeriod> = []; 

            for(const _period of this.extra.periods){
                if (_period.IsValid){
                    _periods.push(_period);
                }
            }

            this._validperiods = _periods;
        }

        return this._validperiods;
    }

    get extraproducts(): Array <ExtraProduct> {
        if (this._validextraproducts == null){
            const _products: Array <ExtraProduct> = []; 

            for(const _product of this.extra.products){
                if (_product.IsValid) {
                    _products.push(_product);
                }
            }

            this._validextraproducts = _products;
        }

        return this._validextraproducts;
    }

    get extraqrcodes(): Array <ExtraTable> {
        if (this._validextratables == null){
            const _products: Array <ExtraTable> = []; 

            for(const _product of this.extra.qrcodes){
                if (_product.IsValid) {
                    _products.push(_product);
                }
            }

            this._validextratables = _products;
        }

        return this._validextratables;
    }

    get products(): Array <ProductView> {
        if (this._validproducts == null){
            const _products: Array <ProductView> = []; 

            for(const _product of this.extraproducts){
                const _candidate = _product.product?.last;
                if (_candidate?.IsValid){
                    _products.push(this.alives.get(_candidate) as ProductView);
                }
            }

            this._validproducts = _products;
        }

        return this._validproducts;
    }

    get qrcodes(): Array <QrCodeView> {
        if (this._validtables == null){
            const _qrcodes: Array <QrCodeView> = [];

            for (const _qrcode of this.extraqrcodes){
                if (_qrcode.qrcode?.IsValid){
                    _qrcodes.push(this.alives.get(_qrcode.qrcode) as QrCodeView);
                }
            }

            this._validtables = _qrcodes;
        }

        return this._validtables;
    }

    /**********************************/
    /* CUSTOM METHODS                 */
    /**********************************/
    
    get IsActive(): boolean {
        if (!this.extra.IsValid){
            return false;
        }

        if (this.extra.allday){
            return true;
        }

        const _now = new Date();
        for (const period of this.periods){
            if (period.IsActiveAt(_now)){
                return true;
            }
        }

        return false;
    }

    /**********************************/
    /* EXTRA APPLICATION              */
    /**********************************/

    AppliesTo(ticket: TicketView): boolean {
        if (!ticket.qrcode || !this.qrcodes.includes(ticket.qrcode)){
            return false;   // does not apply to this table
        }

        if (this.extra.type == 'TICKETFIXED'){
            return true;
        }

        if (!this.extra.allprd){
            const _applies = ticket.products.some((ticketproduct) => {
                return this._appliesTo(ticketproduct);
            })

            if (!_applies){
                return false;   // does not apply to this ticket
            }
        }

        return true;
    }

    ChargeAmount(ticket: TicketView): number {
        if (ticket.products.length == 0){
            return 0;   // do not apply when empty
        }

        if (this.extra.type == 'TICKETFIXED'){
            return this._ticketFixedAmount();
        }

        if (this.extra.type == 'ITEMFIXED'){
            return this._itemFixedAmount(ticket);
        }

        if (this.extra.type == 'ITEMPRCNT'){
            return this._itemPrcntAmount(ticket);
        }

        return 0;   // invalid extra type;
    }

    RefundAmount(ticket: TicketView) : number {
        void ticket;
        return 0;   // TODO
    }

    /**********************************/
    /* PRIVATE METHODS                */
    /**********************************/
    
    private _appliesTo(ticketproduct: TicketProductView): boolean {
        if (this.extra.allprd){
            return true;
        }

        for(const _product of this.extraproducts){
            const _productview = this.alives.get(_product.product) as ProductView;
            if (_productview && (ticketproduct.product == _productview)){
                // the extra product is available in the ticket
                if (_product.options.length == 0){
                    return true;    // no options == all options
                }
                else {  // at least one extra option must be in this ticket product
                    return _product.options.some((extraoption) => {
                        for (const ticketoption of ticketproduct.ticketoptions){
                            if (ticketoption.option && (ticketoption.option == extraoption.option)){
                                return true;    // this extra option available in the ticket
                            }
                        }

                        return false;   // this extra option is not available
                    })
                }
            }
        }
        return false;   // does not apply to this ticket product
    }

    private _ticketFixedAmount(): number {
        return this.extra.price || 0;
    }

    private _itemFixedAmount(ticket: TicketView): number {
        let _amount = 0;

        for (const ticketproduct of ticket.products){
            if (this._appliesTo(ticketproduct)){
                _amount += (ticketproduct.amount || 0) * (this.extra.price || 0);
            }
        }

        return _amount;
    }

    private _itemPrcntAmount(ticket: TicketView): number {
        let _amount = 0;

        for (const ticketproduct of ticket.products){
            if (this._appliesTo(ticketproduct)){
                _amount += (ticketproduct.amount || 0) * (((ticketproduct.calcinfo.charge || 0) * (this.extra.price || 0)) / 100);
            }
        }

        return _amount;
    }
}

/****************************/
/* PROXY VIEW               */
/****************************/

export type ExtraView = ViewType <_ExtraView, Extra>;

export function ExtraViewProxy(object: Extra, data: dataService): ExtraView {
    return new _ExtraView(object, data).Proxy as ExtraView;
}
